import React, {useEffect, useState} from "react";
import styles from './ServiceListContainer.module.css';
import dayjs from 'dayjs';
import axios from "../../../utils/api/axios";
import { IAM_SERVER_SERVICE_USER } from "../../../utils/Config";
import { DialogEnum, showDialog } from "../../../utils/dialog/Dialog";

/**
 * 센서 리스트 Container
 * @param id
 * @param detailList
 * @param update
 * @returns {JSX.Element}
 * @constructor
 */
export const ServiceListContainer = ({id, detailList, update, isMobileWindow}) => {

    const [cellData, setCellData] = useState([]);
    
    // 페이지네이션 상태 추가
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5; // 한 페이지에 보여줄 항목 수
    
    const [openCardId, setOpenCardId] = useState(null); 
    
    useEffect(() => {
        if (detailList && detailList.length > 0) {
            const processed = detailList.map(detail => ({            
                id: detail.linkServiceUserId,
                serviceName: detail.linkServiceId,
                createdDate: dayjs(detail.createdDate).format('YYYY-MM-DD'),
                lastDate: detail.lastConnectionDate === 0 ? "-" : dayjs(detail.lastConnectionDate).format('YYYY-MM-DD HH:mm:ss'),
                status: detail.status
            }));
            setCellData(processed);
        } else {
            setCellData([]);
        }
        setCurrentPage(1);
    }, [detailList]);

    const handleWithdrawal = (item) => {
        const confirmText = "서비스를 탈퇴 하시겠습니까?\n탈퇴 시 서비스의 모든 정보가 삭제됩니다.";

        showDialog(DialogEnum.CONFIRM, "상태 변경 확인", confirmText, () => {
            const url = `${IAM_SERVER_SERVICE_USER}/delete/${item.id}`;

            axios.post(url)
                .then((res) => {
                    const updated = cellData.filter((row) => row.serviceName !== item.serviceName);
                    setCellData(updated);

                    showDialog(DialogEnum.SUCCESS, "완료", "서비스가 성공적으로 탈퇴되었습니다.");
                })
                .catch((e) => {
                    console.error("서비스 탈퇴 실패:", e);
                    showDialog(DialogEnum.DEFAULT, "오류", "서비스 탈퇴 중 오류가 발생했습니다.");
                });
        });
    };

    const handleToggleStatus = (item) => {
        const updatedStatus = !item.status;

        const confirmText = updatedStatus ? "활성화 하시겠습니까?" : "비활성화 하시겠습니까?";

        showDialog(DialogEnum.CONFIRM, "상태 변경 확인", confirmText, () => {
            const url = `${IAM_SERVER_SERVICE_USER}/update/status`;

            const data = {
                identityProvidorId: id,
                linkServiceId: item.serviceName,
                status: updatedStatus
            };

            axios.post(url, data)
                .then((res) => {
                    const updated = cellData.map((row) =>
                        row.serviceName === item.serviceName
                            ? { ...row, status: updatedStatus }
                            : row
                    );
                    setCellData(updated);

                    showDialog(DialogEnum.SUCCESS, "완료", "상태가 변경되었습니다.");
                })
                .catch((e) => {
                    console.error("상태 변경 실패:", e);
                    showDialog(DialogEnum.DEFAULT, "오류", "상태 변경 중 오류가 발생했습니다.");
                });
        });
    };

    const handleCardToggle = (itemId) => {
        setOpenCardId(prevOpenCardId => 
            prevOpenCardId === itemId ? null : itemId
        );
    };

    // 페이지네이션 관련 계산
    const totalPages = Math.ceil(cellData.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = cellData.slice(indexOfFirstItem, indexOfLastItem);

    // ✅ 페이지 변경 핸들러
    const handleNextPage = () => {
        setCurrentPage(prev => Math.min(prev + 1, totalPages));
    };

    const handlePrevPage = () => {
        setCurrentPage(prev => Math.max(prev - 1, 1));
    };

    return (
        <div className={styles.serviceListSection}>
            <p className={styles.serviceListTitle}>서비스 정보</p>

            {/* --- 모바일용 카드 리스트 --- */}
            <div className={styles.serviceCardList}>
                {cellData.length === 0 ? (
                    <p>가입 된 서비스가 없습니다.</p>
                ) : (
                    currentItems.map((item, index) => (
                        <div key={index} className={styles.serviceCard}>
                            <div className={styles.cardMainContent}>
                                <div className={styles.cardInfo}>
                                    <div className={styles.cardRow}>
                                        <span className={styles.cardLabel}>서비스 명</span>
                                        <span className={styles.cardValue}>{item.serviceName}</span>
                                    </div>
                                    <div className={styles.cardRow}>
                                        <span className={styles.cardLabel}>가입일</span>
                                        <span className={styles.cardValue}>{item.createdDate}</span>
                                    </div>
                                    <div className={styles.cardRow}>
                                        <span className={styles.cardLabel}>마지막 접속일</span>
                                        <span className={styles.cardValue}>{item.lastDate}</span>
                                    </div>
                                </div>
                                
                                <button className={styles.cardToggleButton} onClick={() => handleCardToggle(item.id)}>
                                    <span className={`${styles.arrowIcon} ${openCardId === item.id ? styles.arrowOpen : ''}`}>
                                        &gt;
                                    </span>
                                </button>
                            </div>

                            {/* 카드가 열려 있을 때만 액션 버튼 표시 */}
                            {openCardId === item.id && (
                                <div className={styles.cardActions}>
                                    <button
                                        className={`${styles.statusButton} ${item.status ? styles.gray : styles.green}`}
                                        onClick={() => handleToggleStatus(item)}
                                    >
                                        {item.status ? '비활성화' : '활성화'}
                                    </button>
                                    <button className={`${styles.statusButton} ${styles.red}`} onClick={() => handleWithdrawal(item)}>탈퇴</button>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            {totalPages > 1 && (
                <div className={styles.paginationControls}>
                    <button onClick={handlePrevPage} disabled={currentPage === 1} className={styles.arrowButton}>
                        &lt;
                    </button>
                    <span>{currentPage} / {totalPages}</span>
                    <button onClick={handleNextPage} disabled={currentPage === totalPages} className={styles.arrowButton}>
                        &gt;
                    </button>
                </div>
            )}
        </div>
    )
}